import { motion, useInView } from 'motion/react';
import { useRef } from 'react';
import Link from 'next/link';
import { Award, FileText, ArrowRight } from 'lucide-react';

const MENTIONS = [
  { label: 'Tech Partner of the Year', year: '2024', icon: Award },
  { label: 'Nordic Design Award', year: '2023', icon: Award },
  { label: 'Best in Healthcare UX', year: '2023', icon: Award },
  { label: 'Growth Company of the Year', year: '2022', icon: Award },
  { label: 'Innovation in Development', year: '2022', icon: Award },
  { label: 'Featured in Press', year: '2021', icon: FileText },
];

export function BuiltByMentionsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <h2 className="heading-2 mb-4">Built by Mentions</h2>
            <p className="text-lg text-gray-500 leading-relaxed mb-8">
              Our work is featured and recognized by leading industry publications and partners.
            </p>
            <div className="relative aspect-[4/3] max-h-[320px] rounded-2xl overflow-hidden bg-gray-100">
              <img
                src="/hero-frontpage.png"
                alt="Identio team at work"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {MENTIONS.map((item, index) => {
                const Icon = item.icon;
                return (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, y: 16 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.4, delay: 0.15 + index * 0.05 }}
                    className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.08)] hover:border-gray-300 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{item.label}</p>
                      <p className="text-sm text-gray-500">{item.year}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link
                href="/awards"
                className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-medium px-6 py-3 rounded-lg transition-colors"
              >
                Our Awards
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="/press"
                className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 font-medium px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Press Release
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
